import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';

export type ResponsiveBreakpoint = 'mobile' | 'minimal' | 'compact' | 'full';

export interface ToolbarState {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  responsiveBreakpoint: ResponsiveBreakpoint;
  viewportWidth: number;
}

const ToolbarStateContext = createContext<ToolbarState | undefined>(undefined);

export interface ToolbarStateProviderProps {
  children: ReactNode;
  initialExpanded?: boolean;
}

// Helper function to determine responsive breakpoint
const getResponsiveBreakpoint = (width: number): ResponsiveBreakpoint => {
  if (width < 500) return 'mobile';
  if (width < 800) return 'minimal';
  if (width < 1200) return 'compact';
  return 'full';
};

export const ToolbarStateProvider: React.FC<ToolbarStateProviderProps> = ({
  children,
  initialExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [responsiveBreakpoint, setResponsiveBreakpoint] = useState<ResponsiveBreakpoint>(
    getResponsiveBreakpoint(window.innerWidth)
  );

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newBreakpoint = getResponsiveBreakpoint(newWidth);

      setViewportWidth(newWidth);
      setResponsiveBreakpoint(newBreakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const value: ToolbarState = useMemo(() => ({
    isExpanded,
    setIsExpanded,
    responsiveBreakpoint,
    viewportWidth,
  }), [isExpanded, responsiveBreakpoint, viewportWidth]);

  return (
    <ToolbarStateContext.Provider value={value}>
      {children}
    </ToolbarStateContext.Provider>
  );
};

export const useToolbarState = (): ToolbarState => {
  const context = useContext(ToolbarStateContext);
  if (context === undefined) {
    // Return default values when context is not available (graceful fallback)
    return {
      isExpanded: false,
      setIsExpanded: () => {
        // No-op fallback when context is not available
      },
      responsiveBreakpoint: getResponsiveBreakpoint(window.innerWidth),
      viewportWidth: window.innerWidth,
    };
  }
  return context;
};

export default ToolbarStateContext;